import React from 'react'
import DocumentDialog, { DocumentSection } from './DocumentDialog'
import type { ShipmentDocument, DocumentType } from '@wove/api'
import CommercialInvoiceDialog from './document-types/CommercialInvoiceDialog'
import PackingListDialog from './document-types/PackingListDialog'
import AirWaybillDialog from './document-types/AirWaybillDialog'
import BillOfLadingDialog from './document-types/BillOfLadingDialog'
import CertificateOfOriginDialog from './document-types/CertificateOfOriginDialog'
import DangerousGoodsDeclarationDialog from './document-types/DangerousGoodsDeclarationDialog'
import SafetyDataSheetDialog from './document-types/SafetyDataSheetDialog'
import InsuranceCertificateDialog from './document-types/InsuranceCertificateDialog'
import APInvoiceDialog from './document-types/APInvoiceDialog'
import ARInvoiceDialog from './document-types/ARInvoiceDialog'
import BookingConfirmationDialog from './document-types/BookingConfirmationDialog'
import ISFFilingDialog from './document-types/ISFFilingDialog'
import PaymentConfirmationDialog from './document-types/PaymentConfirmationDialog'
import CustomsDeclarationDialog from './document-types/CustomsDeclarationDialog'

export interface DocumentDialogConfig {
  documentType: string
  schema: any
  sections: DocumentSection[]
  component?: React.ComponentType<any>
  additionalTabs?: {
    id: string
    label: string
    content: React.ReactNode
  }[]
}

export interface DocumentDialogRouterProps {
  shipmentId: string
  documentId: string
  document: ShipmentDocument
  configs: Record<string, DocumentDialogConfig>
  fallbackConfig?: DocumentDialogConfig
  useDocumentHistory?: (shipmentId: string, documentId: string) => any
  useUpdateDocument?: (shipmentId: string, documentId: string) => any
  onSave?: (updatedData: any, changes?: any[]) => void
}

export const DocumentDialogRouter: React.FC<DocumentDialogRouterProps> = ({
  shipmentId,
  documentId,
  document,
  configs,
  fallbackConfig,
  useDocumentHistory,
  useUpdateDocument,
  onSave,
}) => {
  // Use specific dialog components based on document type
  switch (document.type) {
    case 'CommercialInvoice':
      return (
        <CommercialInvoiceDialog
          invoiceData={document.extractedData || {}}
          schema={{}} // Schema will be derived from sections
          shipmentId={shipmentId}
          documentId={documentId}
          onSave={onSave || (() => {})}
        />
      )

    case 'PackingList':
      return (
        <PackingListDialog
          packingListData={document.extractedData || {}}
          schema={{}} // Schema will be derived from sections
          shipmentId={shipmentId}
          documentId={documentId}
          onSave={onSave || (() => {})}
        />
      )

    case 'BillOfLading':
      return (
        <BillOfLadingDialog
          data={document.extractedData || {}}
          shipmentId={shipmentId}
          documentId={documentId}
          onSave={onSave || (() => {})}
        />
      )

    case 'AirWaybill':
      return (
        <AirWaybillDialog
          data={document.extractedData || {}}
          shipmentId={shipmentId}
          documentId={documentId}
          onSave={onSave || (() => {})}
        />
      )

    case 'CertificateOfOrigin':
      return (
        <CertificateOfOriginDialog
          data={document.extractedData || {}}
          shipmentId={shipmentId}
          documentId={documentId}
          onSave={onSave || (() => {})}
        />
      )

    case 'SafetyDataSheet':
      return (
        <SafetyDataSheetDialog
          data={document.extractedData || {}}
          shipmentId={shipmentId}
          documentId={documentId}
          onSave={onSave || (() => {})}
        />
      )

    case 'DangerousGoodsDeclaration':
      return (
        <DangerousGoodsDeclarationDialog
          data={document.extractedData || {}}
          shipmentId={shipmentId}
          documentId={documentId}
          onSave={onSave || (() => {})}
        />
      )

    case 'InsuranceCertificate':
      return (
        <InsuranceCertificateDialog
          data={document.extractedData || {}}
          shipmentId={shipmentId}
          documentId={documentId}
          onSave={onSave || (() => {})}
        />
      )

    case 'APInvoice':
      return (
        <APInvoiceDialog
          data={document.extractedData || {}}
          shipmentId={shipmentId}
          documentId={documentId}
          onSave={onSave || (() => {})}
        />
      )

    case 'ARInvoice':
      return (
        <ARInvoiceDialog
          data={document.extractedData || {}}
          shipmentId={shipmentId}
          documentId={documentId}
          onSave={onSave || (() => {})}
        />
      )

    case 'BookingConfirmation':
      return (
        <BookingConfirmationDialog
          data={document.extractedData || {}}
          shipmentId={shipmentId}
          documentId={documentId}
          onSave={onSave || (() => {})}
        />
      )

    case 'ISFFiling':
      return (
        <ISFFilingDialog
          data={document.extractedData || {}}
          shipmentId={shipmentId}
          documentId={documentId}
          onSave={onSave || (() => {})}
        />
      )

    case 'PaymentConfirmation':
      return (
        <PaymentConfirmationDialog
          data={document.extractedData || {}}
          shipmentId={shipmentId}
          documentId={documentId}
          onSave={onSave || (() => {})}
        />
      )

    case 'CustomsDeclaration':
      return (
        <CustomsDeclarationDialog
          data={document.extractedData || {}}
          shipmentId={shipmentId}
          documentId={documentId}
          onSave={onSave || (() => {})}
        />
      )

    default:
      // For other document types, use the generic approach
      const config = configs[document.type] || fallbackConfig

      if (!config) {
        return (
          <div className="p-4 text-center">
            <h2 className="text-lg font-semibold text-gray-900">Unsupported Document Type</h2>
            <p className="text-gray-600 mt-2">
              Document type "{document.type}" is not supported by this dialog system.
            </p>
          </div>
        )
      }

      // If a custom component is specified, use it
      if (config.component) {
        const CustomComponent = config.component
        return (
          <CustomComponent
            documentData={document.extractedData}
            schema={config.schema}
            shipmentId={shipmentId}
            documentId={documentId}
            onSave={onSave || (() => {})}
          />
        )
      }

      // Use the generic DocumentDialog with the configuration
      return (
        <DocumentDialog
          documentData={document.extractedData || {}}
          schema={config.schema}
          sections={config.sections}
          documentType={config.documentType}
          shipmentId={shipmentId}
          documentId={documentId}
          additionalTabs={config.additionalTabs}
          onSave={onSave || (() => {})}
        />
      )
  }
}

// Utility function to create standard document sections
export const createStandardSections = (documentType: string, schema: any): DocumentSection[] => {
  const sections: DocumentSection[] = []

  // Create sections based on schema properties
  if (schema?.properties) {
    const properties = Object.keys(schema.properties)

    // Group related fields into sections
    const basicInfo = properties.filter(prop =>
      ['number', 'date', 'reference', 'type'].some(keyword => prop.toLowerCase().includes(keyword)),
    )

    const parties = properties.filter(prop =>
      ['shipper', 'consignee', 'notifyParty', 'agent', 'carrier'].some(keyword => prop.toLowerCase().includes(keyword)),
    )

    const cargo = properties.filter(prop =>
      ['cargo', 'goods', 'commodity', 'package', 'container'].some(keyword => prop.toLowerCase().includes(keyword)),
    )

    const financial = properties.filter(prop =>
      ['amount', 'value', 'cost', 'charge', 'freight', 'total'].some(keyword => prop.toLowerCase().includes(keyword)),
    )

    const transport = properties.filter(prop =>
      ['port', 'place', 'vessel', 'voyage', 'flight', 'route'].some(keyword => prop.toLowerCase().includes(keyword)),
    )

    // Add sections if they have fields
    if (basicInfo.length > 0) {
      sections.push({
        id: 'basic-info',
        title: 'Basic Information',
        layout: 'double',
        fields: basicInfo.map(prop => ({
          type: 'field',
          label: formatLabel(prop),
          path: prop,
        })),
      })
    }

    if (parties.length > 0) {
      sections.push({
        id: 'parties',
        title: 'Parties',
        layout: 'single',
        fields: parties.map(prop => ({
          type: schema.properties[prop]?.type === 'object' ? 'object' : 'field',
          label: formatLabel(prop),
          path: prop,
          fields:
            schema.properties[prop]?.type === 'object'
              ? Object.keys(schema.properties[prop].properties || {}).map(subProp => ({
                  label: formatLabel(subProp),
                  path: subProp,
                }))
              : undefined,
        })),
      })
    }

    if (cargo.length > 0) {
      sections.push({
        id: 'cargo',
        title: 'Cargo Details',
        layout: 'grid',
        fields: cargo.map(prop => ({
          type: schema.properties[prop]?.type === 'array' ? 'array' : 'field',
          label: formatLabel(prop),
          path: prop,
          columns:
            schema.properties[prop]?.type === 'array'
              ? generateColumnsFromSchema(schema.properties[prop].items)
              : undefined,
        })),
      })
    }

    if (financial.length > 0) {
      sections.push({
        id: 'financial',
        title: 'Financial Information',
        layout: 'double',
        fields: financial.map(prop => ({
          type: 'field',
          label: formatLabel(prop),
          path: prop,
        })),
      })
    }

    if (transport.length > 0) {
      sections.push({
        id: 'transport',
        title: 'Transport Details',
        layout: 'double',
        fields: transport.map(prop => ({
          type: 'field',
          label: formatLabel(prop),
          path: prop,
        })),
      })
    }
  }

  return sections
}

// Helper function to format property names as labels
const formatLabel = (prop: string): string => {
  return prop
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim()
}

// Helper function to generate columns from array item schema
const generateColumnsFromSchema = (itemSchema: any) => {
  if (!itemSchema?.properties) return []

  return Object.keys(itemSchema.properties).map(prop => ({
    label: formatLabel(prop),
    path: prop,
    complexType: inferComplexType(prop, itemSchema.properties[prop]),
  }))
}

// Helper function to infer complex types from schema
const inferComplexType = (
  propName: string,
  propSchema: any,
): 'weight' | 'volume' | 'dimensions' | 'currency' | undefined => {
  const name = propName.toLowerCase()

  if (name.includes('weight') || name.includes('mass')) {
    return 'weight'
  }
  if (name.includes('volume') || name.includes('cubic')) {
    return 'volume'
  }
  if (name.includes('dimension') || name.includes('size')) {
    return 'dimensions'
  }
  if (name.includes('amount') || name.includes('value') || name.includes('cost') || name.includes('price')) {
    return 'currency'
  }

  // Check schema structure for complex types
  if (propSchema?.properties) {
    const props = Object.keys(propSchema.properties)

    if (props.includes('value') && props.includes('unit')) {
      if (name.includes('weight')) return 'weight'
      if (name.includes('volume')) return 'volume'
    }

    if (props.includes('amount') && props.includes('currency')) {
      return 'currency'
    }

    if (props.includes('length') && props.includes('width') && props.includes('height')) {
      return 'dimensions'
    }
  }

  return undefined
}

// Predefined configurations for common document types
export const createDefaultConfigs = (): Record<string, DocumentDialogConfig> => {
  return {
    CommercialInvoice: {
      documentType: 'Commercial Invoice',
      schema: {}, // Will be provided by the application
      sections: [], // Will be generated from schema
    },
    BillOfLading: {
      documentType: 'Bill of Lading',
      schema: {},
      sections: [],
    },
    PackingList: {
      documentType: 'Packing List',
      schema: {},
      sections: [],
    },
    AirWaybill: {
      documentType: 'Air Waybill',
      schema: {},
      sections: [],
    },
    CertificateOfOrigin: {
      documentType: 'Certificate of Origin',
      schema: {},
      sections: [],
    },
    SafetyDataSheet: {
      documentType: 'Safety Data Sheet',
      schema: {},
      sections: [],
    },
    DangerousGoodsDeclaration: {
      documentType: 'Dangerous Goods Declaration',
      schema: {},
      sections: [],
    },
    InsuranceCertificate: {
      documentType: 'Insurance Certificate',
      schema: {},
      sections: [],
    },
  }
}

// Hook for building dialog configurations
export const useDocumentDialogConfigs = (schemas: Record<string, any>) => {
  const configs = React.useMemo(() => {
    const defaultConfigs = createDefaultConfigs()

    // Update configs with provided schemas and generate sections
    Object.keys(defaultConfigs).forEach(docType => {
      if (schemas[docType]) {
        defaultConfigs[docType].schema = schemas[docType]
        defaultConfigs[docType].sections = createStandardSections(docType, schemas[docType])
      }
    })

    return defaultConfigs
  }, [schemas])

  return configs
}
